import React, { useState, useEffect } from "react"
import { useForm, SubmitHandler } from "react-hook-form"
import axios from "axios"

import InputField from "../Inputs/InputField"
import SelectField from "../Inputs/SelectField"
import TextareaField from "../Inputs/TextareaField"
import DateField from "../Inputs/DateField"
import FileField from "../Inputs/FileField"
import CheckboxGroup from "../Inputs/CheckboxGroup"
import CountrySelect from "../Inputs/CountrySelect"

import useFlavours from "../../../hooks/useFlavours"
import { TITLE_SELECT } from "../../../utils/utils_constants"
import {
  convertBase64,
  buildAssetUpload,
  formatFormDate,
  getPlainText,
} from "../../../utils"
import { AssetUpload } from "../../../models/common"
import useDictionary from "../../../hooks/useDictionary"

interface Props {
  workingHandler?: Function
  pageContext: any
}

const defaultProps = {}

type FormValues = {
  Title: string
  "First Name": string
  "Last Name": string
  "Daytime Phone": string
  Email: string
  "Confirm Email": string
  Question: string
  Subject: string
  "Email OptIn": string[] | Boolean
}

export const AskADietitian: React.FC<Props> = props => {
  // hooks
  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    setError,
    formState,
    control,
  } = useForm<FormValues>()

  // state
  const [working, setWorking] = useState(false)
  const [errorSubmitting, setErrorSubmitting] = useState(false)
  // state
  const [formFunctions, setFormFunctions] = useState({
    register: register,
    handleSubmit: handleSubmit,
    errors: formState.errors,
    trigger: trigger,
    getValues: getValues,
    setValue: setValue,
    control: control,
  })

  useEffect(() => {
    setFormFunctions({
      register: register,
      handleSubmit: handleSubmit,
      errors: formState.errors,
      trigger: trigger,
      getValues: getValues,
      setValue: setValue,
      control: control,
    })
  }, [
    formState.errors,
    register,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    control,
  ])


  // events
  const onSubmit: SubmitHandler<FormValues> = async (data: any) => {
    // validate email field comparison
    if (data["Email"] !== data["Confirm Email"]) {
      setError("Email", {
        type: "custom",
        message: "Emails do not match",
      })
      setError("Confirm Email", {
        type: "custom",
        message: "Emails do not match",
      })
    } else {
      setWorking(true)
      if (props.workingHandler) {
        props.workingHandler(true)
      }
      setErrorSubmitting(false)
      const formData = new FormData()
      const fileKeys = ["Image 1", "Image 2", "Image 3"]

      Object.entries(data).forEach(([key, value]) => {
        if (fileKeys.includes(key)) {
          if ((value as any[])[0] != null) {
            formData.append(key, (value as any[])[0], (value as any[])[0].name)
          }
        } else {
          formData.append(key, value as any)
        }
      })

      const sfEndPoint =
        "https://mcr42t499sfpsj528-hlv5qh4smm.rest.marketingcloudapis.com/messaging/v1/messageDefinitionSends/key:10753/send"
      const sfImageEndPoint = ""
      const sfImageCategory = ""
      const sfRecordType = "ContactForm"
      const emailRecipient = "kalel.johnston@sanitarium.com.au"
      //const emailRecipient = "nutrition@sanitarium.com.au"

      formData.append("emailRecipient", emailRecipient as any)
      formData.append("sfEndPoint", sfEndPoint as any)
      formData.append("sfImageEndPoint", sfImageEndPoint as any)
      formData.append("sfRecordType", sfRecordType as any)
      formData.append("sfImageCategory", sfImageCategory as any)
      const sf_response = await axios.post("/api/form", formData)

      if (
        (sf_response.status == 200 || sf_response.status == 202) &&
        sf_response.data.success
      ) {
        document.location.href = "/au/about/contact/nutritionist-thank-you"
      } else {
        setWorking(false)
        if (props.workingHandler) {
          props.workingHandler(false)
        }
        setErrorSubmitting(true)
      }
    }
  }

  return (
    <section className="form">
      <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
        <div className="grid-x grid-margin-x">
          <div className="cell small-11 large-6">
            <div className="form__heading">
              {useDictionary("Ask a dietitian", props.pageContext.locale)}
            </div>
            <SelectField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="Title"
              label={useDictionary("Title", props.pageContext.locale)}
              placeholder="Select"
              options={TITLE_SELECT}
            />
            <InputField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="First Name"
              label={useDictionary("First Name", props.pageContext.locale)}
              maxLength={255}
            />
            <InputField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="Last Name"
              label={useDictionary("Last Name", props.pageContext.locale)}
              maxLength={100}
            />

            <InputField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="Daytime Phone"
              label={useDictionary("Daytime Phone", props.pageContext.locale)}
              maxLength={50}
            />
            <InputField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              type="email"
              label={useDictionary("Email Address", props.pageContext.locale)}
              name="Email"
              required
              maxLength={254}
            />
            <InputField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              type="email"
              name="Confirm Email"
              label={useDictionary("Confirm Email", props.pageContext.locale)}
              required
              maxLength={254}
            />
          </div>
          <div className="cell small-11 large-6">
            <div className="form__heading">&nbsp;</div>
            <SelectField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="Subject"
              label={useDictionary("Subject", props.pageContext.locale)}
              placeholder={useDictionary("Subject", props.pageContext.locale)}
              required
              options={[
                {
                  value: "Weet-Bix Cholesterol Lowering",
                  label: useDictionary(
                    "Weet-Bix Cholesterol Lowering",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "Fitness & Exercise",
                  label: useDictionary(
                    "Fitness & Exercise",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "Health Conditions",
                  label: useDictionary(
                    "Health Conditions",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "Healthy Eating",
                  label: useDictionary(
                    "Healthy Eating",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "Kids Health",
                  label: useDictionary("Kids Health", props.pageContext.locale),
                },
                {
                  value: "Meat-Free Living",
                  label: useDictionary(
                    "Meat-Free Living",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "Nutrition Information",
                  label: useDictionary(
                    "Nutrition Information",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "Soy",
                  label: useDictionary("Soy", props.pageContext.locale),
                },
                {
                  value: "Weight Management",
                  label: useDictionary(
                    "Weight Management",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "Wellbeing",
                  label: useDictionary("Wellbeing", props.pageContext.locale),
                },
                {
                  value: "Women's Health",
                  label: useDictionary(
                    "Women's Health",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "General Question",
                  label: useDictionary(
                    "General Question",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "Sanitarium Recipes",
                  label: useDictionary(
                    "Sanitarium Recipes",
                    props.pageContext.locale
                  ),
                },
                {
                  value: "Ingredient Information",
                  label: useDictionary(
                    "Ingredient Information",
                    props.pageContext.locale
                  ),
                },
              ]}
            />
            <TextareaField
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="Question"
              label={useDictionary("Question", props.pageContext.locale)}
              maxLength={4000}
            />

            <CheckboxGroup
              pageContext={props.pageContext}
              formFunctions={formFunctions}
              name="Email OptIn"
              label={useDictionary("Email OptIn", props.pageContext.locale)}
              hideLabel
              options={[
                {
                  value: "yes",
                  label: useDictionary(
                    "Sign me up for Sanitarium products news, initiatives, community events and special offers. Delivered direct to your inbox monthly",
                    props.pageContext.locale
                  ),
                },
              ]}
            />
          </div>
        </div>

        <div className="form__cta">
          <input
            type="submit"
            className="button"
            value={
              working
                ? useDictionary("Please wait", props.pageContext.locale)
                : useDictionary("Send details", props.pageContext.locale)
            }
            disabled={working}
          />
        </div>

        {errorSubmitting && (
          <>
            <p className="error">
              {useDictionary(
                "Something went wrong, please try again.",
                props.pageContext.locale
              )}
            </p>
          </>
        )}
      </form>
    </section>
  )
}

AskADietitian.defaultProps = defaultProps

export default AskADietitian
